import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export const DiscountsTab = () => {
  const [discounts, setDiscounts] = useState([]);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/discounts").then(({ data }) => setDiscounts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/discounts", { code, type, value: parseFloat(value) || 0, active: true });
      toast.success(`Code ${code.toUpperCase()} created`);
      setCode("");
      setValue("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (d) => {
    try {
      await api.put(`/admin/discounts/${d.id}`, { code: d.code, type: d.type, value: d.value, active: !d.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete code ${d.code}?`)) return;
    await api.delete(`/admin/discounts/${d.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div data-testid="discounts-tab">
      <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight uppercase mb-6">Discount Codes</h2>

      <form onSubmit={create} className="flex flex-wrap gap-3 mb-8 bg-[#171412] border border-[#2C2623] p-4" data-testid="discount-create-form">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="CODE"
          required
          className="bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm font-mono uppercase tracking-widest w-40 transition-colors"
          data-testid="discount-code-input"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-xs font-mono uppercase tracking-widest transition-colors"
          data-testid="discount-type-select"
        >
          <option value="percent">% off</option>
          <option value="fixed">$ off</option>
        </select>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={type === "percent" ? "15" : "10.00"}
          required
          type="number"
          step="0.01"
          min="0"
          className="bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm w-28 transition-colors"
          data-testid="discount-value-input"
        />
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-[#E05A36] text-[#0C0A09] font-bold uppercase text-xs tracking-wider px-5 py-2.5 hover:bg-[#F06B47] transition-colors disabled:opacity-50"
          data-testid="discount-create-button"
        >
          <Plus className="w-4 h-4" /> Create code
        </button>
      </form>

      <div className="overflow-x-auto border border-[#2C2623]">
        <table className="w-full text-sm" data-testid="discounts-table">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067] border-b border-[#2C2623]">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2C2623]">
            {discounts.map((d) => (
              <tr key={d.id} className="hover:bg-[#1E1A17] transition-colors" data-testid={`discount-row-${d.code}`}>
                <td className="px-4 py-3 font-mono font-bold text-[#E29D47]">{d.code}</td>
                <td className="px-4 py-3">{d.type === "percent" ? `${d.value}% off` : `$${d.value.toFixed(2)} off`}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggle(d)}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors ${d.active ? "text-[#7BC47F] border-[#7BC47F]/40" : "text-[#7A7067] border-[#2C2623]"}`}
                    data-testid={`discount-toggle-${d.code}`}
                  >
                    {d.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(d)} className="p-2 text-[#B8AEA5] hover:text-[#E05A36] transition-colors" data-testid={`discount-delete-${d.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {discounts.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-[#7A7067] font-mono text-xs uppercase tracking-widest">No codes yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
